import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { useAuthState } from "../app/AuthStateContext";
import SteamSignInButton from "./SteamSignInButton";
import PlaySafeSignInButton from "./PlaySafeSignInButton";
import { useTemplateTrack } from "../templates/TemplateTrackingProvider";

/**
 * The canonical stacked sign-in panel.
 *
 * Every supported SSO provider is shown together, on purpose: a lone
 * "Continue with Steam" button on a stark page reads like a phishing page to a
 * CS2 audience that's been burned by inventory scams. Seeing several real,
 * recognisable providers (Steam, Google, and soon Playsafe ID) — plus the
 * read-only assurance line — signals a legitimate product with options rather
 * than a single suspicious gate.
 *
 * Used by the homepage hero and footer callout, and ready for the header /
 * other templates. To add a provider, render it in the secondary stack below
 * the divider (see the Playsafe ID slot).
 */
const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: 300,
    mx: "auto",
  },
  steamButton: {
    minHeight: 52,
    px: 2.5,
    gap: 1.25,
    borderRadius: "999px",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    backgroundColor: "#E8A33D",
    color: "#0B0D0F",
    transition: "background-color 0.15s ease-in-out",
    "&:hover": { backgroundColor: "#B97A1F" },
  },
  assurance: {
    mt: 1.25,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    color: "rgba(255,255,255,0.42)",
    fontSize: "0.75rem",
    lineHeight: 1.4,
    textAlign: "center",
  },
  divider: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    my: 2.25,
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  dividerText: {
    fontSize: "0.7rem",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.32)",
    fontWeight: 600,
  },
  secondaryStack: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 1.25,
  },
  termsLine: {
    mt: 1.5,
    textAlign: "center",
    color: "rgba(255,255,255,0.32)",
    fontSize: "0.7rem",
    lineHeight: 1.5,
  },
  termsLink: {
    color: "inherit",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    "&:hover": { color: "rgba(255,255,255,0.55)" },
  },
  googleWrap: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    // GoogleLogin renders its own iframe; keep it on its native light scheme
    // so the branded button paints correctly regardless of page color-scheme.
    colorScheme: "light",
  },
};

export default function AuthOptions({ surface = "hero" }) {
  const { googleResponse, onFailure } = useAuthState();
  const track = useTemplateTrack();

  return (
    <Box sx={styles.root}>
      <SteamSignInButton
        sx={styles.steamButton}
        showAssurance={false}
        surface={surface}
        fullWidth
      />

      <Box sx={styles.assurance}>
        <LockOutlinedIcon sx={{ fontSize: 13 }} />
        <Typography
          component="span"
          sx={{ fontSize: "inherit", color: "inherit" }}
        >
          Read-only sign-in. We can&apos;t touch your inventory, trades, or items.
          New accounts need public CS2 hours.
        </Typography>
      </Box>

      <Box sx={styles.divider}>
        <Box sx={styles.dividerLine} />
        <Typography component="span" sx={styles.dividerText}>
          or
        </Typography>
        <Box sx={styles.dividerLine} />
      </Box>

      <Box sx={styles.secondaryStack}>
        <Box
          sx={styles.googleWrap}
          onClick={() => track("sso_google_clicked", { provider: "google" })}
        >
          <GoogleLogin
            shape="pill"
            size="large"
            theme="filled_black"
            text="continue_with"
            width="300"
            onSuccess={googleResponse}
            onError={onFailure}
          />
        </Box>

        <PlaySafeSignInButton surface={surface} fullWidth />
      </Box>

      <Typography component="p" sx={styles.termsLine}>
        By continuing, you agree to our{" "}
        <Typography
          component={Link}
          to="/terms-of-use"
          target="_blank"
          rel="noopener noreferrer"
          sx={styles.termsLink}
        >
          Terms of Service
        </Typography>
        .
      </Typography>
    </Box>
  );
}

AuthOptions.propTypes = {
  surface: PropTypes.string,
};
